import axios from 'axios';
import apiClient from '@/api/apiClient';
import type { Feedback, FeedbackPage, FeedbackQuery, UpdateFeedbackInput } from '../types';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface ApiErrorBody {
  code?: number | string;
  message?: string;
  result?: Record<string, string[]>;
}

export class FeedbacksApiError extends Error {
  code?: number | string;
  status?: number;
  fieldErrors?: Record<string, string[]>;

  constructor(
    message: string,
    options: { code?: number | string; status?: number; fieldErrors?: Record<string, string[]> } = {},
  ) {
    super(message);
    this.name = 'FeedbacksApiError';
    this.code = options.code;
    this.status = options.status;
    this.fieldErrors = options.fieldErrors;
  }
}

export const toFeedbacksApiError = (error: unknown): FeedbacksApiError => {
  if (error instanceof FeedbacksApiError) return error;
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    return new FeedbacksApiError(
      error.response?.data?.message
        || (error.code === 'ECONNABORTED'
          ? 'Yêu cầu đã quá thời gian chờ. Vui lòng thử lại.'
          : error.response
            ? 'Máy chủ không thể thực hiện yêu cầu.'
            : 'Không thể kết nối đến máy chủ.'),
      {
        code: error.response?.data?.code,
        status: error.response?.status,
        fieldErrors: error.response?.data?.result,
      },
    );
  }
  return new FeedbacksApiError(
    error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.',
  );
};

const withNormalizedError = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error: unknown) {
    throw toFeedbacksApiError(error);
  }
};

export const getFeedbacks = (query: FeedbackQuery): Promise<FeedbackPage> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<FeedbackPage>>('/api/feedbacks', { params: query });
    return response.data.result;
  });

export const getFeedbackById = (feedbackId: number): Promise<Feedback> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<Feedback>>(`/api/feedbacks/${feedbackId}`);
    return response.data.result;
  });

export const updateFeedback = (feedbackId: number, input: UpdateFeedbackInput): Promise<Feedback> =>
  withNormalizedError(async () => {
    const response = await apiClient.put<ApiResponse<Feedback>>(`/api/feedbacks/${feedbackId}`, input);
    return response.data.result;
  });

export const deleteFeedback = (feedbackId: number): Promise<void> =>
  withNormalizedError(async () => {
    await apiClient.delete(`/api/feedbacks/${feedbackId}`);
  });
